'use client';

import { Box, Button } from '@chakra-ui/react';
import { createElement, type ReactNode } from 'react';
import { EmptyState, LoadingState } from '@/components/shared/ui';
import { promptTopicFilterNeedsInventory, type PromptTopicFilter } from '@/utils/promptTopicFilter';

interface PromptInventoryBoundaryProps {
  filter?: PromptTopicFilter;
  ready?: boolean;
  error?: string | null;
  invalid?: boolean;
  onRetry: () => unknown;
  onClear?: () => unknown;
  children?: ReactNode;
}

function PromptInventoryBoundary({
  filter,
  ready,
  error,
  invalid,
  onRetry,
  onClear,
  children,
}: PromptInventoryBoundaryProps) {
  const needsInventory = promptTopicFilterNeedsInventory(filter);
  const inventoryError = needsInventory ? error : null;
  const pending = needsInventory && !ready && !inventoryError;
  const unavailable = Boolean(invalid && (!needsInventory || ready));
  const blocked = pending || Boolean(inventoryError) || unavailable;

  return (
    <>
      {blocked ? (
        <Box p="4" data-prompt-inventory-state="" role={pending ? 'status' : 'alert'}>
          {pending ? (
            <LoadingState message="Loading selected prompts…" />
          ) : (
            <EmptyState
              title={
                inventoryError ? 'Unable to load selected prompts' : 'Topic filter unavailable'
              }
              description={inventoryError || 'Update or clear the topic filter to continue.'}
              action={
                inventoryError ? (
                  <Button size="sm" onClick={() => void onRetry()}>
                    Retry
                  </Button>
                ) : onClear ? (
                  <Button size="sm" onClick={() => void onClear()}>
                    Clear topic filter
                  </Button>
                ) : undefined
              }
            />
          )}
        </Box>
      ) : null}
      {/* Keep toolbar portals mounted while the report content is hidden. */}
      <Box
        display={blocked ? 'none' : 'contents'}
        data-prompt-inventory-content=""
        aria-hidden={blocked || undefined}
      >
        {children}
      </Box>
    </>
  );
}

export function withPromptInventoryBoundary(
  content: ReactNode,
  props: Omit<PromptInventoryBoundaryProps, 'children'>
) {
  return createElement(PromptInventoryBoundary, props, content);
}
